'use client'

import type { ColumnDef } from '@tanstack/react-table'

import { VaultCell } from '@morpho-org/ui-v6/client'
import { CellSimpleText } from '@morpho-org/ui-v6/server'
import { formatters } from '@morpho-org/ui-v6/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import {
  createPaginatedRowModel,
  createSortedRowModel,
  rowPaginationFeature,
  rowSortingFeature,
  sortFns,
  tableFeatures
} from '@tanstack/react-table'

import RiskCuratorCell from '@/components/common/Cells/RiskCuratorCell'
import { sortingStringFn } from '@/helpers/sorting'

import type { VaultListingTableRow } from './VaultListing.types'

export const vaultListingTableFeatures = tableFeatures({
  rowPaginationFeature,
  rowSortingFeature,
  paginatedRowModel: createPaginatedRowModel(),
  sortedRowModel: createSortedRowModel(),
  sortFns
})
export type VaultListingTableFeatures = typeof vaultListingTableFeatures

type VaultListingColumnDef = ColumnDef<VaultListingTableFeatures, VaultListingTableRow>
export const columnsMarketAllocation: VaultListingColumnDef[] = [
  {
    id: 'vault',
    accessorKey: 'vault',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Vault</span>
        </TooltipTrigger>
        <TooltipContent>The vault that supplies this market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const vault = row.original.vault

      return (
        <VaultCell
          nameClassName="text-2xs max-w-[240px]"
          address={vault.address}
          name={vault.name}
          logoURI={vault.assetLogoUri ?? undefined}
          chainId={vault.chainId}
          warnings={vault.warnings}
          isVaultV2={row.original.isVaultV2}
        />
      )
    },
    sortFn: (a, b) => sortingStringFn(a.original.vault.name, b.original.vault.name)
  },
  {
    id: 'RiskCurator',
    accessorKey: 'vault',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '14px' }}>
            Curator
          </span>
        </TooltipTrigger>
        <TooltipContent>The market Curator.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <RiskCuratorCell
        curators={row.original.vault.curators}
        chainId={row.original.vault.chainId}
        textClassName="text-2xs"
      />
    ),
    enableSorting: false
  },
  {
    id: 'allocation',
    accessorFn: ({ allocation }) => allocation.totalSupplyAssetsUsd ?? undefined,
    sortUndefined: 'last',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Allocation</span>
        </TooltipTrigger>
        <TooltipContent>The amount the vault has supplied into this market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const totalSupplyAssetsUsd = row.original.allocation.totalSupplyAssetsUsd
      return (
        <CellSimpleText>
          {totalSupplyAssetsUsd === null
            ? '-'
            : formatters.short.trailingZero(false).digits(2).unit('$').of(totalSupplyAssetsUsd)}
        </CellSimpleText>
      )
    },
    sortFn: 'basic'
  }
]

export const columnLabelsVaultListing = {
  vault: 'vault name',
  allocation: 'total supply'
}
